import { Link, useParams } from 'react-router';

import { useFundTransaction } from '@/hooks/react-query/useFundTransactions';
import type { FundTransactionType } from '@/types/fund-transactions';

const FUND_TRANSACTION_PARENT_BREADCRUMB: Record<
  FundTransactionType,
  { label: string; href: (fundId: string) => string }
> = {
  DISTRIBUTION: {
    label: 'Distributions',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=distributions`,
  },
  MANAGEMENT_FEE: {
    label: 'Management Fees',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=management-fees`,
  },
  MANAGEMENT_FEE_RECOGNITION: {
    label: 'Management Fees',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=management-fees`,
  },
  FUND_COST: {
    label: 'Fund Costs',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=fund-costs`,
  },
  FUND_COST_RECOGNITION: {
    label: 'Fund Costs',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=fund-costs`,
  },
  DEAL_COST: {
    label: 'Deal Costs',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=fees-and-costs&subsection=deal-costs`,
  },
  DRAWDOWN: {
    label: 'Drawdowns',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=drawdowns`,
  },
  CURRENCY_CONVERSION: {
    label: 'Currency Conversions',
    href: (fundId) =>
      `/app/funds/${fundId}?tab=transactions&section=currency-conversions`,
  },
  FX_PERIOD_END: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  ASSET_SALE: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  INVESTMENT_PURCHASE: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  FINANCING_ROUND: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  CONVERTIBLE_INVESTMENT: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  CONVERTIBLE_CONVERSION: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
  CONVERTIBLE_REPAYMENT: {
    label: 'Transactions',
    href: (fundId) => `/app/funds/${fundId}?tab=transactions&section=all`,
  },
};

/**
 * Dynamic parent-segment breadcrumb for the fund-transaction detail route.
 * Renders the correct section link based on the transaction type, falling back
 * to a generic "Transactions" link while the transaction is loading.
 */
export function FundTransactionParentBreadcrumb() {
  const { fundId = '', transactionId = '' } = useParams<{
    fundId: string;
    transactionId: string;
  }>();
  const { data: transaction } = useFundTransaction(fundId, transactionId);

  const entry = transaction
    ? FUND_TRANSACTION_PARENT_BREADCRUMB[transaction.transaction_type]
    : null;

  const label = entry?.label ?? 'Transactions';
  const href = entry
    ? entry.href(fundId)
    : `/app/funds/${fundId}?tab=transactions&section=all`;

  return (
    <Link to={href} className="hover:text-foreground transition-colors">
      {label}
    </Link>
  );
}
